import React, { useState, useEffect, useMemo } from 'react'
import { Button, Modal, message, Empty, Icon, Spin } from 'antd'
// Reusing styles from LocalReferencesView as requested
import '../LocalReferences/LocalReferencesView.scss'
import {
  findUsagesForProp,
  validatePropKey,
  calculateNextPropKey
} from './utils/PropUtils'
import PropItem from './components/PropItem'
import AddPropModal from './components/AddPropModal'
import BatchConfirmModal from './components/BatchConfirmModal'
import DeletePropModal from './components/DeletePropModal'
import ImageEditorModal from '../../../ImageGeneration/components/ImageEditorModal'

const PropReferencesView = ({
  formData = {},
  propMap = {},
  propReference = {},
  scenes = [],
  setFormData,
  onRegeneratePropReference,
  onEditPropReference,
  isGeneratingPropReference,
  onBatchUpdateProps,
  propGenerationProgress,
  propGenerationStatus,
  videoId
}) => {
  const [pendingEdits, setPendingEdits] = useState({})
  const displayMap = useMemo(
    () => ({
      ...(propMap || {}),
      ...pendingEdits
    }),
    [propMap, pendingEdits]
  )

  const keys = Object.keys(displayMap)

  const [modalVisible, setModalVisible] = useState(false)
  const [modalKey, setModalKey] = useState(null)
  const [modalUsages, setModalUsages] = useState([])
  const [replacementKey, setReplacementKey] = useState(null)
  const [regenerateModalVisible, setRegenerateModalVisible] = useState(false)
  const [editingKey, setEditingKey] = useState(null)
  const [editValue, setEditValue] = useState('')

  const [addModalVisible, setAddModalVisible] = useState(false)
  const [newPropKey, setNewPropKey] = useState('')
  const [newPropDescription, setNewPropDescription] = useState('')

  const [batchConfirmVisible, setBatchConfirmVisible] = useState(false)

  const [confirmSaveModalVisible, setConfirmSaveModalVisible] = useState(false)
  const [pendingSaveKey, setPendingSaveKey] = useState(null)

  const [generatingKeys, setGeneratingKeys] = useState(null)
  const [loadingTip, setLoadingTip] = useState('')

  const [showImageEditorModal, setShowImageEditorModal] = useState(false)
  const [currentEditingPropKey, setCurrentEditingPropKey] = useState(null)

  const openDeleteModal = key => {
    const usages = findUsagesForProp(key, scenes)
    setModalKey(key)
    setModalUsages(usages)
    setReplacementKey(null)
    setModalVisible(true)
  }

  const startEditing = (key, currentDescription) => {
    setEditingKey(key)
    setEditValue(currentDescription || '')
  }

  const cancelEditing = () => {
    setEditingKey(null)
    setEditValue('')
  }

  const saveEditing = key => {
    setPendingSaveKey(key)
    setConfirmSaveModalVisible(true)
  }

  const handleOpenImageEditor = key => {
    setCurrentEditingPropKey(key)
    setShowImageEditorModal(true)
  }

  const handleImageEditorConfirm = imageUrl => {
    if (!currentEditingPropKey || !imageUrl) return

    const key = currentEditingPropKey
    const placeholderKey = `__${key}__`

    setFormData(prev => {
      const newRefs = { ...(prev.propReference || {}) }

      // Update both potential keys
      const baseRef = newRefs[placeholderKey] || newRefs[key] || {}
      const updatedRef = {
        ...baseRef,
        uri: imageUrl,
        url: imageUrl,
        image: imageUrl,
        src: imageUrl,
        tag: baseRef.tag || key
      }

      newRefs[placeholderKey] = updatedRef
      if (newRefs.hasOwnProperty(key)) {
        newRefs[key] = updatedRef
      }

      return {
        ...prev,
        propReference: newRefs
      }
    })

    setShowImageEditorModal(false)
    setCurrentEditingPropKey(null)
    message.success(`Prop image updated for ${key}`)
  }

  const handleConfirmSave = () => {
    if (!pendingSaveKey) return

    setPendingEdits(prev => ({
      ...prev,
      [pendingSaveKey]: editValue
    }))
    message.success('Prop description updated (unsaved)')

    setEditingKey(null)
    setEditValue('')
    setConfirmSaveModalVisible(false)
    setPendingSaveKey(null)
  }

  useEffect(() => {
    if (!isGeneratingPropReference) {
      setGeneratingKeys(null)
      setLoadingTip('')
    }
  }, [isGeneratingPropReference])

  const handleDelete = () => {
    const key = modalKey
    const usages = modalUsages || []

    const otherKeys = Object.keys(displayMap || {}).filter(k => k !== key)

    if (usages.length > 0 && otherKeys.length > 0 && !replacementKey) {
      message.error(
        'Please select an existing prop to map prompts to before deleting'
      )
      return
    }

    try {
      let updatedScenes = scenes
      if (replacementKey) {
        const oldPlaceholder = `__${key}__`
        const newPlaceholder = `__${replacementKey}__`

        const replaceInText = text => {
          if (!text || typeof text !== 'string') return text
          return text
            .split(oldPlaceholder)
            .join(newPlaceholder)
            .split(key)
            .join(replacementKey)
        }

        updatedScenes = scenes.map(scene => {
          const newScene = { ...scene }
          if (newScene.description)
            newScene.description = replaceInText(newScene.description)
          if (Array.isArray(newScene.shots)) {
            newScene.shots = newScene.shots.map(shot => {
              const s = { ...shot }
              if (s.title) s.title = replaceInText(s.title)
              if (s.initial_image_prompt)
                s.initial_image_prompt = replaceInText(s.initial_image_prompt)
              if (s.video_prompt) s.video_prompt = replaceInText(s.video_prompt)
              if (s.dialogue) {
                const newDialogue = {}
                Object.entries(s.dialogue).forEach(([char, val]) => {
                  const text = typeof val === 'object' ? val.text : val
                  const newText = replaceInText(text)
                  if (typeof val === 'object') {
                    newDialogue[char] = { ...val, text: newText }
                  } else {
                    newDialogue[char] = newText
                  }
                })
                s.dialogue = newDialogue
              }
              return s
            })
          }
          return newScene
        })
      }

      // Remove from propReference
      const newRefs = { ...(propReference || {}) }
      const placeholderKey = `__${key}__`
      if (newRefs.hasOwnProperty(placeholderKey)) delete newRefs[placeholderKey]
      if (newRefs.hasOwnProperty(key)) delete newRefs[key]

      // If key was in pendingEdits, remove it
      if (pendingEdits[key]) {
        const newPending = { ...pendingEdits }
        delete newPending[key]
        setPendingEdits(newPending)
      }

      const currentPropMap = propMap || {}
      if (currentPropMap.hasOwnProperty(key)) {
        const newMap = { ...currentPropMap }
        if (newMap.hasOwnProperty(key)) delete newMap[key]
        if (newMap.hasOwnProperty(placeholderKey)) delete newMap[placeholderKey]
        setFormData(prev => ({
          ...prev,
          shotList: updatedScenes,
          propReference: newRefs,
          propMap: newMap
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          shotList: updatedScenes,
          propReference: newRefs
        }))
      }

      setModalVisible(false)
      message.success('Prop reference deleted')
    } catch (err) {
      console.error('Failed to delete prop reference', err)
      message.error('Failed to delete prop reference')
    }
  }

  const handleAddProp = () => {
    const nextKey = calculateNextPropKey(Object.keys(displayMap || {}))
    setNewPropKey(nextKey)
    setNewPropDescription('')
    setAddModalVisible(true)
  }

  const submitAddProp = () => {
    if (!newPropKey || !newPropDescription) {
      message.error('Please provide description')
      return
    }

    if (!validatePropKey(newPropKey)) {
      message.error(
        'Prop key must follow the format: __propX__ (e.g., __prop1__)'
      )
      return
    }

    if (displayMap && displayMap.hasOwnProperty(newPropKey)) {
      message.error('Prop key already exists')
      return
    }

    setPendingEdits(prev => ({
      ...prev,
      [newPropKey]: newPropDescription
    }))
    setAddModalVisible(false)
    message.success('Prop added (unsaved)')
  }

  const handleSaveBatch = () => {
    if (!onBatchUpdateProps) return
    const modifiedKeys = Object.keys(pendingEdits)
    if (modifiedKeys.length === 0) return

    setBatchConfirmVisible(true)
  }

  const confirmBatchGeneration = () => {
    const modifiedKeys = Object.keys(pendingEdits)

    setFormData(prev => ({
      ...prev,
      propMap: {
        ...(prev.propMap || {}),
        ...pendingEdits
      }
    }))

    setPendingEdits({})
    setBatchConfirmVisible(false)
    onBatchUpdateProps(displayMap, modifiedKeys)
    setGeneratingKeys(modifiedKeys)
    setLoadingTip('Regenerating props...')
  }

  return (
    <div className="local-references-container">
      <header className="lr-header">
        <div className="lr-info">
          <h2 className="lr-title">
            Props List ({Object.keys(displayMap || {}).length} items)
          </h2>
          <p className="lr-description">
            Prop references are used to generate prop-specific images for
            consistent props in your video.
          </p>
        </div>
        <div className="lr-toolbar">
          {Object.keys(pendingEdits).length > 0 && (
            <Button
              type="primary"
              onClick={handleSaveBatch}
              disabled={isGeneratingPropReference}
              icon={<Icon type="save" />}
            >
              Save & Regenerate ({Object.keys(pendingEdits).length})
            </Button>
          )}

          <div className="lr-list-actions">
            <Button
              disabled={isGeneratingPropReference}
              onClick={handleAddProp}
              icon="plus"
            >
              Add Prop
            </Button>
            <Button
              disabled={isGeneratingPropReference}
              onClick={() => {
                setRegenerateModalVisible(true)
              }}
              icon="reload"
            >
              Regenerate All Props
            </Button>
          </div>
        </div>
      </header>

      <div className="lr-body">
        {!keys.length ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Empty
              description={
                <span style={{ color: '#8c8c8c' }}>
                  No prop references defined yet
                </span>
              }
            />
            <Button
              type="primary"
              onClick={handleAddProp}
              style={{ marginTop: 24 }}
              icon="plus"
            >
              Add First Prop
            </Button>
          </div>
        ) : (
          <div className="lr-content-wrapper">
            <div className="lr-items-grid">
              {keys.map(key => (
                <PropItem
                  key={key}
                  propKey={key}
                  description={displayMap[key]}
                  isPending={pendingEdits.hasOwnProperty(key)}
                  propReference={propReference}
                  isGeneratingPropReference={isGeneratingPropReference}
                  generatingKeys={generatingKeys}
                  loadingTip={loadingTip}
                  propGenerationProgress={propGenerationProgress}
                  propGenerationStatus={propGenerationStatus}
                  onEdit={startEditing}
                  onDelete={openDeleteModal}
                  onSave={saveEditing}
                  onCancelEdit={cancelEditing}
                  editingKey={editingKey}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  onOpenImageEditor={handleOpenImageEditor}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <DeletePropModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onDelete={handleDelete}
        modalKey={modalKey}
        modalUsages={modalUsages}
        displayMap={displayMap}
        propReference={propReference}
        replacementKey={replacementKey}
        setReplacementKey={setReplacementKey}
      />

      {/* Regenerate confirmation modal */}
      <Modal
        visible={regenerateModalVisible}
        title="Regenerate prop references"
        onCancel={() => setRegenerateModalVisible(false)}
        onOk={() => {
          setRegenerateModalVisible(false)
          if (onRegeneratePropReference) {
            try {
              onRegeneratePropReference()
              setGeneratingKeys('ALL')
              setLoadingTip('Regenerating all prop references...')
              message.info('Regenerating prop references...')
            } catch (err) {
              console.error('Failed to trigger regenerate:', err)
              message.error('Failed to start regeneration')
            }
          }
        }}
        okText="Yes, regenerate"
        cancelText="Cancel"
        centered
      >
        <p>
          This will overwrite existing prop references. Are you sure you want to
          continue?
        </p>
      </Modal>

      <AddPropModal
        visible={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={submitAddProp}
        newPropKey={newPropKey}
        newPropDescription={newPropDescription}
        setNewPropDescription={setNewPropDescription}
      />

      {/* Confirm Save Modal */}
      <Modal
        visible={confirmSaveModalVisible}
        title="Update Draft Description"
        onCancel={() => {
          setConfirmSaveModalVisible(false)
          setPendingSaveKey(null)
        }}
        onOk={handleConfirmSave}
        okText="Update Draft"
        cancelText="Cancel"
        centered
      >
        <p>
          This will update the description in your draft. You must click 'Save &
          Regenerate' to apply these changes and generate new images.
        </p>
      </Modal>

      <BatchConfirmModal
        visible={batchConfirmVisible}
        onCancel={() => setBatchConfirmVisible(false)}
        onOk={confirmBatchGeneration}
        pendingEdits={pendingEdits}
      />

      <ImageEditorModal
        videoId={videoId}
        visible={showImageEditorModal}
        onCancel={() => {
          setShowImageEditorModal(false)
          setCurrentEditingPropKey(null)
        }}
        onConfirm={handleImageEditorConfirm}
        mode="nanobanana"
        {...(() => {
          if (!currentEditingPropKey) return {}

          const key = currentEditingPropKey
          const description = displayMap[key]
          const refs = propReference || {}
          const refKey = refs[`__${key}__`] ? `__${key}__` : key
          const reference = refs[refKey]
          const imageSrc =
            reference &&
            (reference.uri || reference.url || reference.image || reference.src)

          // Gather reference images from scenes
          const allProjectImages = scenes.flatMap(s =>
            (s.shots || []).map(sh => ({
              url: sh.imageSrc,
              shotTitle: `Scene ${s.id} - Shot ${sh.id}`,
              sceneTitle: `Scene ${s.id}`
            }))
          )

          // Add other prop references
          const propRefs = []
          if (propReference) {
            Object.entries(propReference).forEach(([k, v]) => {
              if (k === key || k === `__${key}__`) return // Skip current one
              const url =
                typeof v === 'string'
                  ? v
                  : v?.uri || v?.url || v?.image || v?.src
              if (url) {
                propRefs.push({
                  url: url,
                  shotTitle: k.replace(/__/g, ''),
                  sceneTitle: 'Prop'
                })
              }
            })
          }

          return {
            initialPrompt: description,
            currentImage: imageSrc,
            aspectRatio: formData.aspectRatio || '16:9',
            referenceImages: [...allProjectImages, ...propRefs].filter(
              img => img.url
            )
          }
        })()}
      />
    </div>
  )
}

export default PropReferencesView
